import { ASSIGNED_SEATING, enums } from "@shared/constants";
import { t } from "@shared/utils/internationalization";
import throttle from "lodash/throttle";
import { SeatingItemPropertyMap } from "../types/seating-property-types";
import SeatingService from "./SeatingService";
import StageService from "./StageService";

export type TooltipParamType = {
  id: number;
  identifier: string;
  is_accessible?: boolean; // only for seats
  is_limited_visibility?: boolean; // only for seats
  location_identifier?: string; // only for seats
  name: string;
  segment_identifier: string;
};

export type TooltipReturnType = {
  details: {
    name: string;
    segment_identifier: string;
    location_identifier?: string; // only for seats
    identifier: string;
    segmentText: string;
    purchaser?: {
      name_on_ticket: string;
      email: string;
      phone_number?: string;
      invoice_id?: number;
      basket_id?: number;
    };
    status?: string;
  };
  itemTypes: Array<SeatingItemPropertyMap>;
  isAccessible: boolean;
  isLimitedVisibility: boolean;
  isResale: boolean;
};

export default class SeatingMapService {
  public stageService: StageService;

  public seatingService: SeatingService;

  constructor(stageService: StageService, seatingService: SeatingService) {
    this.stageService = stageService;
    this.seatingService = seatingService;
  }

  /**
   * Create the content to be displayed in the seat tooltips
   */
  setTooltipContent(data: TooltipParamType): TooltipReturnType {
    const {
      id,
      identifier,
      is_accessible: isAccessible = false,
      is_limited_visibility: isLimitedVisibility = false,
      location_identifier,
      name,
      segment_identifier,
    } = data;

    const details: TooltipReturnType["details"] = {
      name,
      segment_identifier,
      location_identifier,
      identifier,
      segmentText: "",
    };
    let itemTypes: Array<SeatingItemPropertyMap> = [];
    let isResale = false;

    if (name === ASSIGNED_SEATING.SEAT.ID) {
      const permissions = this.seatingService.seatingMap[id];
      isResale = Boolean(
        permissions?.find(
          (p) =>
            p.seatingItemResaleId &&
            p.seatingItemResaleStatus ===
              enums.RESALE_SUBMISSION_STATUS_MAP.PENDING.id
        )
      );

      itemTypes =
        permissions &&
        permissions.map(
          (p) => this.seatingService.itemTypeMap[p.seatingItemTypeId]
        );

      if (permissions?.[0]?.seatPurchaser) {
        details.purchaser = permissions[0].seatPurchaser;
      }
      if (permissions?.[0]?.seatStatus) {
        details.status = permissions[0].seatStatus;
      }
    }

    if (name === ASSIGNED_SEATING.GENERAL_ADMISSION.ID) {
      const permissions = this.seatingService.locationMap[id];

      itemTypes =
        permissions &&
        permissions.map(
          (p) => this.seatingService.itemTypeMap[p.seatingItemTypeId]
        );
    }

    if (name === ASSIGNED_SEATING.SEGMENT.ID) {
      if (this.seatingService.soldOutSegments.includes(id)) {
        details.segmentText = t("Section Sold Out");
      }
    }

    return {
      details,
      itemTypes,
      isAccessible,
      isLimitedVisibility,
      isResale,
    };
  }

  /**
   * Handles window resize.
   * Resizes the stage canvas element.
   */
  handleWindowResize(element: HTMLElement): TimerHandler {
    return throttle(() => {
      this.stageService.setStageSize(element.offsetWidth, element.offsetHeight);
      // center stage on resize
      this.stageService.centerStage();
    }, 250);
  }

  /**
   * Sets the canvas map container element
   */
  static setCanvasContainer(canvasId: string): HTMLElement | null {
    return document?.getElementById(canvasId);
  }
}
